import { useEffect, useState } from 'react';
import { Users2, Gift, Share2, Copy, MessageCircle, X, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { referrals as referralsApi } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Refer-a-Friend button + share sheet.
 *
 * Every phone-verified friend earns the referrer 200 MandiCoins (once the
 * friend places their first order). Coins convert to Khata credit at ₹1/coin,
 * so 200 coins = ₹5 off the next Khata bill (plus a Loyalty Badge that grows
 * with successful referrals).
 */
export default function ReferAFriendButton({ variant = 'inline' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const cls = variant === 'inline'
    ? 'inline-flex items-center gap-2 rounded-lg bg-[#0D3F12] hover:bg-[#0A320E] text-white font-black uppercase tracking-wider text-xs h-10 px-4'
    : 'inline-flex items-center gap-2 rounded-lg border-2 border-[#0D3F12] text-[#0D3F12] hover:bg-[#E8F5E9] font-black uppercase tracking-wider text-xs h-10 px-4';

  return (
    <>
      <button
        data-testid={`refer-friend-btn-${variant}`}
        onClick={() => setOpen(true)}
        className={cls}
      >
        <Users2 className="w-4 h-4"/>Refer a friend
      </button>
      {open && <ShareDialog onClose={() => setOpen(false)}/>}
    </>
  );
}

function ShareDialog({ onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referralsApi.me().then(setData).catch(() => {});
  }, []);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Copied to clipboard');
    } catch { toast.error('Copy failed'); }
  };

  const openWhatsApp = () => {
    const msg = encodeURIComponent(data.share_text);
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  const nativeShare = async () => {
    if (navigator.share && data) {
      try {
        await navigator.share({ title: 'BharatCart', text: data.share_text, url: data.share_url });
      } catch { /* user cancelled */ }
    } else if (data) {
      copy(data.share_text);
    }
  };

  const successful = data?.successful ?? 0;
  const badgeTier = successful >= 10 ? 'Gold Referrer' : successful >= 5 ? 'Silver Referrer' : successful >= 1 ? 'Bronze Referrer' : null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="refer-share-dialog" className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-ink/40 hover:text-ink" aria-label="Close">
          <X className="w-5 h-5"/>
        </button>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-black text-[#0D3F12]">
          <Gift className="w-4 h-4"/>Refer & Earn
        </div>
        <h3 className="font-heading font-black text-2xl mt-1">Bring a friend, earn ₹5 Khata credit</h3>
        <p className="text-xs text-ink/70 mt-1">
          Every friend who signs up with your code and completes phone verification earns you <b>200 MandiCoins</b> (₹5 off your next Khata bill) — plus a growing Loyalty Badge.
        </p>

        {!data ? (
          <div className="mt-6 flex items-center gap-2 text-ink/50"><Loader2 className="w-4 h-4 animate-spin"/>Loading your code…</div>
        ) : (
          <>
            <div className="mt-4 rounded-lg bg-[#F1F8E9] border-2 border-[#0D3F12] p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#0D3F12]">Your referral code</div>
              <div data-testid="refer-code" className="mt-1 font-mono text-2xl font-black text-ink tracking-wider">{data.code}</div>
              <button
                data-testid="refer-copy-code"
                onClick={() => copy(data.code)}
                className="mt-1 inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider text-[#0D47A1] hover:underline"
              >
                {copied ? <Check className="w-3 h-3"/> : <Copy className="w-3 h-3"/>}Copy code
              </button>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-muted p-2">
                <div className="font-heading font-black text-lg text-ink">{data.total_invited || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/60">Invited</div>
              </div>
              <div className="rounded-lg bg-muted p-2">
                <div className="font-heading font-black text-lg text-[#1B5E20]">{successful}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/60">Verified</div>
              </div>
              <div className="rounded-lg bg-muted p-2">
                <div className="font-heading font-black text-lg text-[#B26900]">₹{Math.round((data.coins_earned || 0) / 40)}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/60">Earned</div>
              </div>
            </div>

            {badgeTier && (
              <div data-testid="refer-badge" className="mt-3 rounded-lg bg-[#FFF3E0] border border-[#FFCC80] p-2 text-xs font-black text-[#8D3A0A] text-center">
                🏅 You're a <b>{badgeTier}</b> — thanks for bringing your village along!
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              <button
                data-testid="refer-whatsapp"
                onClick={openWhatsApp}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white font-black uppercase text-xs tracking-wider h-11"
              >
                <MessageCircle className="w-4 h-4"/>Share on WhatsApp
              </button>
              <button
                data-testid="refer-share"
                onClick={nativeShare}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-ink text-ink font-black uppercase text-xs tracking-wider h-11"
              >
                <Share2 className="w-4 h-4"/>Share to anywhere
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
